import { useEffect, useRef, useState } from 'react';

// Props:
//   onScan(code)     — called only for NEW unique barcodes
//   active           — true = camera on, false = camera off
//   scannedCodes     — array of already-scanned barcodes (never scan these again)
export default function BarcodeScanner({ onScan, active = true, scannedCodes = [] }) {
  const videoRef      = useRef(null);
  const streamRef     = useRef(null);
  const animRef       = useRef(null);
  const onScanRef     = useRef(onScan);
  const sessionCodes  = useRef(new Set()); // tracks ALL codes scanned this session
  const lastCode      = useRef('');
  const lastTime      = useRef(0);
  const detectorRef   = useRef(null);
  const [camError, setCamError] = useState('');
  const [flash, setFlash]       = useState(false);

  // Keep callback fresh
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  // Sync already-scanned codes into session set
  useEffect(() => {
    scannedCodes.forEach(c => sessionCodes.current.add(c.toUpperCase()));
  }, [scannedCodes]);

  // ── Camera start/stop ─────────────────────────────────────────────────
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width:  { ideal: 1280 },
          height: { ideal: 720  },
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      setCamError('Camera blocked. Allow camera access then reload.');
      return false;
    }
    return true;
  };

  const stopCamera = () => {
    cancelAnimationFrame(animRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  // ── Handle a detected code ────────────────────────────────────────────
  const handleDetected = (code) => {
    const clean = code.trim().toUpperCase();
    const now   = Date.now();

    // 1. Already scanned in this session → silent ignore, no vibrate, no flash
    if (sessionCodes.current.has(clean)) return;

    // 2. Same code detected twice within 2s (camera jitter) → ignore
    if (clean === lastCode.current && now - lastTime.current < 2000) return;

    // New unique barcode!
    lastCode.current = clean;
    lastTime.current = now;
    sessionCodes.current.add(clean); // mark as scanned immediately

    // Green flash
    setFlash(true);
    setTimeout(() => setFlash(false), 350);

    // Single vibrate
    if (navigator.vibrate) navigator.vibrate(100);

    // Beep
    try {
      const ctx  = new (window.AudioContext || window.webkitAudioContext)();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 1800;
      gain.gain.value     = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.08);
    } catch {}

    onScanRef.current(clean);
  };

  // ── Native BarcodeDetector (fast — Chrome Android) ────────────────────
  const startNativeDetector = async () => {
    const detector = new window.BarcodeDetector({
      formats: ['code_128', 'code_39', 'ean_13', 'ean_8', 'qr_code', 'data_matrix', 'itf']
    });
    detectorRef.current = detector;

    const scan = async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) {
        animRef.current = requestAnimationFrame(scan);
        return;
      }
      try {
        const found = await detector.detect(videoRef.current);
        if (found.length > 0) handleDetected(found[0].rawValue);
      } catch {}
      animRef.current = requestAnimationFrame(scan);
    };
    animRef.current = requestAnimationFrame(scan);
  };

  // ── ZXing fallback ────────────────────────────────────────────────────
  const startZxing = async () => {
    const { BrowserMultiFormatReader, NotFoundException } = await import('@zxing/library');
    const reader = new BrowserMultiFormatReader();
    detectorRef.current = reader;
    try {
      await reader.decodeFromVideoDevice(null, videoRef.current, (result, err) => {
        if (result) handleDetected(result.getText());
        if (err && !(err instanceof NotFoundException)) {}
      });
    } catch {
      setCamError('Scanner failed. Please reload.');
    }
  };

  // ── Start everything ──────────────────────────────────────────────────
  const startScanning = async () => {
    setCamError('');
    const ok = await startCamera();
    if (!ok) return;

    await new Promise(resolve => {
      if (videoRef.current?.readyState >= 2) return resolve();
      videoRef.current.onloadeddata = resolve;
    });

    if ('BarcodeDetector' in window) {
      await startNativeDetector();
    } else {
      await startZxing();
    }
  };

  const stopScanning = () => {
    cancelAnimationFrame(animRef.current);
    if (detectorRef.current?.reset) {
      try { detectorRef.current.reset(); } catch {}
    }
    detectorRef.current = null;
    stopCamera();
  };

  // Reset session when active turns off (new batch)
  useEffect(() => {
    if (!active) {
      sessionCodes.current = new Set();
      lastCode.current = '';
    }
  }, [active]);

  useEffect(() => {
    if (active) {
      startScanning();
    } else {
      stopScanning();
    }
    return () => stopScanning();
  }, [active]);

  return (
    <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
      <video
        ref={videoRef}
        style={{ width: '100%', display: 'block', maxHeight: 230 }}
        muted playsInline autoPlay
      />

      {/* Green flash */}
      {flash && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(74,222,128,0.35)',
          pointerEvents: 'none',
          transition: 'opacity 0.2s'
        }} />
      )}

      {/* Aim box */}
      {!camError && active && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <div style={{ width: '80%', height: 80, position: 'relative' }}>
            {[
              { top: 0,    left: 0,    borderTop: '4px solid #4ade80', borderLeft: '4px solid #4ade80' },
              { top: 0,    right: 0,   borderTop: '4px solid #4ade80', borderRight: '4px solid #4ade80' },
              { bottom: 0, left: 0,    borderBottom: '4px solid #4ade80', borderLeft: '4px solid #4ade80' },
              { bottom: 0, right: 0,   borderBottom: '4px solid #4ade80', borderRight: '4px solid #4ade80' },
            ].map((s, i) => (
              <div key={i} style={{ position: 'absolute', width: 22, height: 22, ...s }} />
            ))}
            <div style={{
              position: 'absolute', left: 0, right: 0, height: 2,
              background: 'linear-gradient(90deg,transparent,#4ade80,transparent)',
              animation: 'scanline 1.2s ease-in-out infinite alternate',
            }} />
          </div>
          <div style={{
            position: 'absolute', bottom: 8,
            background: 'rgba(0,0,0,0.6)', borderRadius: 20,
            padding: '3px 14px', color: 'rgba(255,255,255,0.9)', fontSize: 11
          }}>
            {flash ? '✅ Scanned! Move to next' : 'Align barcode inside the box'}
          </div>
        </div>
      )}

      {/* Error */}
      {camError && (
        <div style={{
          position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.9)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: 16, gap: 10, textAlign: 'center'
        }}>
          <span style={{ fontSize: 32 }}>📵</span>
          <span style={{ color: '#fca5a5', fontSize: 13, lineHeight: 1.5 }}>{camError}</span>
          <button
            onClick={() => { setCamError(''); startScanning(); }}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: '#3b82f6', color: '#fff', fontSize: 13,
              cursor: 'pointer', fontWeight: 600
            }}
          >Try Again</button>
        </div>
      )}

      <style>{`
        @keyframes scanline {
          from { top: 5px }
          to   { top: calc(100% - 7px) }
        }
      `}</style>
    </div>
  );
}
